import { useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Empty,
  Field,
  Input,
  MapLegend,
  Page,
  RideMap,
  Spinner,
  api,
  brl,
  dateTime,
  km,
  minutes,
  rideStatusLabel,
} from '@mobi/web';

interface RideSummary {
  id: number;
  status: string;
  vehicleType: 'car' | 'moto';
  passengerName: string;
  driverName: string | null;
  fareCents: number | null;
  paymentMethod: string | null;
  requestedAt: string;
  finishedAt: string | null;
  cancelledBy: string | null;
  cancelReason: string | null;
  originAddress: string;
  destAddress: string;
  distanceMeters: number;
  durationSeconds: number;
  hasRoute: boolean;
}

interface RideDetail {
  id: number;
  status: string;
  passengerName: string;
  passengerPhone: string | null;
  driverName: string | null;
  driverPhone: string | null;
  origin: { lat: number; lng: number; address: string };
  destination: { lat: number; lng: number; address: string };
  routePolyline: string | null;
}

interface TrailPoint {
  lat: number;
  lng: number;
  recordedAt: string;
}

const STATUSES = [
  'requested', 'offering', 'accepted', 'driver_arrived', 'in_progress', 'finished', 'cancelled', 'no_drivers',
];

export function History() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [status, setStatus] = useState('');
  const [driverId, setDriverId] = useState('');
  const [passengerId, setPassengerId] = useState('');
  const [rides, setRides] = useState<RideSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);

  async function search() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      if (status) params.set('status', status);
      if (driverId) params.set('driverId', driverId);
      if (passengerId) params.set('passengerId', passengerId);
      params.set('limit', '100');
      const res = await api<{ total: number; rides: RideSummary[] }>(`/api/admin/rides?${params}`);
      setRides(res.rides);
      setTotal(res.total);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (openId !== null) {
    return <RideRoute id={openId} onBack={() => setOpenId(null)} />;
  }

  return (
    <Page title="Histórico de corridas" subtitle="Filtre por período, status, motorista ou passageiro.">
      <Card className="mb-5">
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="De">
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label="Até">
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </Field>
          <Field label="Status">
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full rounded-2xl border border-line bg-surface-2 px-4 py-3.5 text-base"
            >
              <option value="">todos</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>{rideStatusLabel(s)}</option>
              ))}
            </select>
          </Field>
          <Field label="ID do motorista">
            <Input inputMode="numeric" value={driverId} onChange={(e) => setDriverId(e.target.value)} />
          </Field>
          <Field label="ID do passageiro">
            <Input inputMode="numeric" value={passengerId} onChange={(e) => setPassengerId(e.target.value)} />
          </Field>
        </div>
        <Button className="mt-4" onClick={search} loading={loading}>
          Filtrar
        </Button>
      </Card>

      {loading ? (
        <div className="grid place-items-center py-16">
          <Spinner />
        </div>
      ) : rides.length === 0 ? (
        <Empty title="Nada encontrado" hint="Ajuste o filtro e tente de novo." />
      ) : (
        <Card className="divide-y divide-line p-0">
          {rides.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => r.hasRoute && setOpenId(r.id)}
              className="flex w-full items-center gap-4 px-5 py-4 text-left hover:bg-surface-2 disabled:cursor-default"
              disabled={!r.hasRoute}
            >
              <div className="min-w-0 flex-1">
                <p className="font-medium">#{r.id} · {r.passengerName} → {r.driverName ?? 'sem motorista'}</p>
                <p className="truncate text-sm text-muted">
                  {r.originAddress} → {r.destAddress}
                </p>
                <p className="text-xs text-faint">
                  {dateTime(r.requestedAt)} · {km(r.distanceMeters)} · {minutes(r.durationSeconds)}
                </p>
              </div>
              <div className="text-right">
                <p className="font-semibold">{brl(r.fareCents)}</p>
                <Badge tone={r.status === 'finished' ? 'good' : r.status === 'cancelled' ? 'bad' : 'neutral'}>
                  {rideStatusLabel(r.status)}
                </Badge>
              </div>
            </button>
          ))}
        </Card>
      )}
      <p className="mt-3 text-sm text-faint">{total} no total</p>
    </Page>
  );
}

/** Rota planejada (linha do roteador) sobreposta à trilha real percorrida pelo motorista. */
function RideRoute({ id, onBack }: { id: number; onBack: () => void }) {
  const [ride, setRide] = useState<RideDetail | null>(null);
  const [trail, setTrail] = useState<TrailPoint[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [rideRes, trailRes] = await Promise.all([
        api<RideDetail>(`/api/admin/rides/${id}`),
        api<{ points: TrailPoint[] }>(`/api/admin/rides/${id}/positions`),
      ]);
      if (!cancelled) {
        setRide(rideRes);
        setTrail(trailRes.points);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  return (
    <Page title={`Corrida #${id}`}>
      <button type="button" onClick={onBack} className="mb-4 text-sm text-muted underline underline-offset-4">
        ← voltar ao histórico
      </button>

      {loading || !ride ? (
        <div className="grid place-items-center py-16">
          <Spinner />
        </div>
      ) : (
        <>
          <RideMap
            markers={[
              { id: 'o', point: ride.origin, kind: 'origin' },
              { id: 'x', point: ride.destination, kind: 'destination' },
            ]}
            routePolyline={ride.routePolyline}
            walkPath={trail.length > 1 ? trail.map((p) => ({ lat: p.lat, lng: p.lng })) : null}
            className="mb-3 h-96 w-full overflow-hidden rounded-3xl border border-line"
          />
          <MapLegend items={[{ kind: 'origin', label: 'Origem' }, { kind: 'destination', label: 'Destino' }]} />
          <p className="mt-2 text-sm text-faint">
            Linha cheia: rota planejada. Linha pontilhada: trajeto real do motorista
            {trail.length > 1 ? ` (${trail.length} pontos amostrados)` : ' — sem amostras suficientes para desenhar.'}
          </p>

          <Card className="mt-5">
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <dt className="text-faint">Passageiro</dt>
              <dd>{ride.passengerName} {ride.passengerPhone && `· ${ride.passengerPhone}`}</dd>
              <dt className="text-faint">Motorista</dt>
              <dd>{ride.driverName ?? '—'} {ride.driverPhone && `· ${ride.driverPhone}`}</dd>
              <dt className="text-faint">Origem</dt>
              <dd>{ride.origin.address}</dd>
              <dt className="text-faint">Destino</dt>
              <dd>{ride.destination.address}</dd>
            </dl>
          </Card>
        </>
      )}
    </Page>
  );
}
